// Grammar of "when may this field move" — t-159.
//
// Three answers, and the mockup marks a field with the one that is not the
// default (.codelane/artifacts/t-159/leaderboard-pending-changes.html, the
// legend strip at §1 781-788):
//
//   cut  — the cycles already cut belong to the old value, so the field lands
//          where a cycle has no history yet. Never accepted by an immediate
//          PATCH.
//   now  — `status` alone. A pause that waited for the next cycle would not be
//          a pause, so it never travels in a package either.
//   lock — `ext_key` alone. It lives in builds already on players' phones and
//          no cycle boundary can make moving it safe.
//
// Everything unmarked goes either way and the save bar is where the operator
// chooses. This module is the ONE place the three lists are written down; the
// form, the save bar, the diff and the tab all read them from here.

import type { Leaderboard, WindowKind } from "./types";

export type WhenBadge = "cut" | "now" | "lock";

/**
 * Only at a cut. Mirrors `leaderboard.ScheduleOnlyFields` in Go — the server
 * refuses each of these in a PATCH, so a form that offered "Save now" for them
 * would be selling a 400.
 */
export const SCHEDULE_ONLY_FIELDS = [
  "window_kind",
  "scope",
  "segmentation",
  "tier_mode",
  "guild_score_source",
  "guild_aggregator",
  "guild_stat_key",
  "window_minutes",
] as const;

/** Never in a package. Mirrors `leaderboard.NeverScheduledFields`. */
export const NEVER_SCHEDULED_FIELDS = ["ext_key", "status"] as const;

const SCHEDULE_ONLY = new Set<string>(SCHEDULE_ONLY_FIELDS);

/**
 * Which of the three marks a field carries, or nothing at all.
 *
 * `event_start_at` / `event_end_at` are deliberately unmarked even though they
 * belong to the window family: an event board's dates are read as the window
 * itself rather than stamped onto rows, so moving them needs no boundary to
 * wait for (spec §4). They stay exactly as editable as they are today.
 */
export function whenBadge(field: string): WhenBadge | undefined {
  if (field === "ext_key") return "lock";
  if (field === "status") return "now";
  return SCHEDULE_ONLY.has(field) ? "cut" : undefined;
}

export const WHEN_LABEL: Record<WhenBadge, string> = {
  cut: "next cycle only",
  now: "applies immediately",
  lock: "locked",
};

/** The legend strip's own shorter wording for the same three marks. */
export const WHEN_LEGEND_LABEL: Record<WhenBadge, string> = {
  cut: "next cycle",
  now: "now",
  lock: "locked",
};

/**
 * Does this board ever cut another cycle? An `event` window closes without a
 * successor and `all_time` never closes at all, so on both there is no moment
 * a package could be applied at — which is why the second save button, and
 * every field that could only travel in one, is not offered there.
 */
export function boardHasNextCycle(kind: WindowKind | undefined): boolean {
  return kind === "recurring" || kind === "recurring_event";
}

/** Fields that used to be settable only when the board was created, and now
   travel in a package. The diff says so on each of them, because an operator
   who knows the old rule will not believe the new one otherwise. */
const WAS_CREATE_ONLY = new Set([
  "window_kind",
  "scope",
  "segmentation",
  "tier_mode",
  "guild_score_source",
]);

export function wasCreateOnly(field: string): boolean {
  return WAS_CREATE_ONLY.has(field);
}

// --- Names ------------------------------------------------------------------

/** What a field is called on this page. The diff and the form say the same
   words, so an operator can find the row a diff line is about. */
export const FIELD_LABEL: Record<string, string> = {
  ext_key: "Ext key",
  name: "Name",
  description: "Description",
  visibility: "Visibility",
  operator: "Operator",
  sort: "Sort",
  submit_policy: "Submit policy",
  realtime: "Realtime",
  guild_score_source: "Score source",
  guild_aggregator: "Guild aggregator",
  guild_stat_key: "Stat key",
  min_guild_members: "Min team size",
  reward_contributors_only: "Reward contributors only",
  late_joiners_may_score: "Late joiners may score",
  window_kind: "Window kind",
  cron: "Cron",
  window_minutes: "Window length",
  event_start_at: "Event start",
  event_end_at: "Event end",
  scope: "Scope",
  segmentation: "Segmentation",
  tier_mode: "Tier mode",
  tier_config: "Tiers",
  bucket_size: "Room size",
  roster_size: "Roster size",
  matchmaking_config: "Matchmaking",
  country_split: "Country split",
  auto_enroll: "Auto enrol",
  guards: "Anti-cheat guards",
  "guards.max_score_per_submit": "Max score per submit",
  "guards.max_delta_per_submit": "Max delta per submit",
  "guards.min_interval_sec": "Min interval",
  hold_finished_cycles: "Hold finished cycles",
  retention_days: "Retention",
  metadata: "Metadata",
  status: "Status",
  ladder_decision: "Player ladder",
  rewards: "Prize table",
};

export function fieldLabel(field: string): string {
  return FIELD_LABEL[field] ?? field;
}

/** The api name a diff row prints under the label. Only the prize table
   differs from the field key: it is a table, not a column. */
export function fieldApiName(field: string): string {
  return field === "rewards" ? "leaderboard_rewards" : field;
}

// --- Sections ---------------------------------------------------------------

/**
 * The card of the form each field is edited in — the diff is grouped by it, so
 * the list reads in the order the operator scrolled through.
 */
export const SECTION_ORDER = [
  "Identity",
  "Scoring",
  "Window",
  "Scope & segmentation",
  "Anti-cheat guards",
  "Cycle lifetime",
  "Metadata",
  "Prize table",
] as const;

export type SectionName = (typeof SECTION_ORDER)[number];

const FIELD_SECTION: Record<string, SectionName> = {
  ext_key: "Identity",
  name: "Identity",
  description: "Identity",
  visibility: "Identity",
  operator: "Scoring",
  sort: "Scoring",
  submit_policy: "Scoring",
  realtime: "Scoring",
  guild_score_source: "Scoring",
  guild_aggregator: "Scoring",
  guild_stat_key: "Scoring",
  min_guild_members: "Scoring",
  reward_contributors_only: "Scoring",
  late_joiners_may_score: "Scoring",
  window_kind: "Window",
  cron: "Window",
  window_minutes: "Window",
  event_start_at: "Window",
  event_end_at: "Window",
  scope: "Scope & segmentation",
  segmentation: "Scope & segmentation",
  tier_mode: "Scope & segmentation",
  tier_config: "Scope & segmentation",
  bucket_size: "Scope & segmentation",
  roster_size: "Scope & segmentation",
  matchmaking_config: "Scope & segmentation",
  country_split: "Scope & segmentation",
  auto_enroll: "Scope & segmentation",
  ladder_decision: "Scope & segmentation",
  guards: "Anti-cheat guards",
  hold_finished_cycles: "Cycle lifetime",
  retention_days: "Cycle lifetime",
  metadata: "Metadata",
  rewards: "Prize table",
};

export function fieldSection(field: string): SectionName {
  return FIELD_SECTION[field.split(".")[0]] ?? "Identity";
}

// --- Values -----------------------------------------------------------------

/** The mockup's own wording for a column that holds nothing yet — drawn
   without the strike-through the other side of a diff carries, because there
   is no old value to cross out. */
export const NOT_SET = "not set";

function tierKeys(value: unknown): string[] {
  const tiers = (value as { tiers?: { key?: string }[] } | null)?.tiers;
  if (!Array.isArray(tiers)) return [];
  return tiers.map((t) => (t?.key ?? "").trim()).filter(Boolean);
}

/**
 * A field's value as one short string. Objects are summarised rather than
 * printed: a diff row is one line, and `tier_config` spelled out as JSON would
 * push the arrow off the page.
 */
export function formatFieldValue(field: string, value: unknown): string {
  if (value === undefined || value === null || value === "") return NOT_SET;
  if (field === "tier_config") {
    const n = tierKeys(value).length;
    return n === 1 ? "1 tier" : `${n} tiers`;
  }
  if (field === "rewards") {
    const n = Array.isArray(value) ? value.length : 0;
    return n === 1 ? "1 row" : `${n} rows`;
  }
  if (field === "metadata") {
    const n = Object.keys(value as object).length;
    return n === 1 ? "1 key" : `${n} keys`;
  }
  if (field === "matchmaking_config") return "rules";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

/** The muted note the mockup pins to the right of some diff rows. */
export function fieldValueNote(
  field: string,
  value: unknown,
): string | undefined {
  if (field === "tier_config") {
    const keys = tierKeys(value);
    return keys.length > 0 ? keys.join(" / ") : undefined;
  }
  return undefined;
}

// --- Consequences -----------------------------------------------------------

/**
 * What the operator will SEE happen, for the handful of changes whose effect
 * is not the field itself. Written in the mockup's own voice: the next cycle
 * gets the new answer, the open one keeps the one it was cut with.
 *
 * Every other field says nothing here on purpose — a consequence line under a
 * renamed board would be noise, and noise is what makes the four that matter
 * unreadable.
 */
export function consequenceFor(
  field: string,
  next: unknown,
  lb: Leaderboard,
): string | undefined {
  switch (field) {
    case "sort":
      return next === "asc"
        ? "The next cycle ranks the lowest score first. The open one keeps its own direction — it was cut under the old one and is paid out by it."
        : "The next cycle ranks the highest score first. The open one keeps its own direction — it was cut under the old one and is paid out by it.";
    case "window_kind":
      if (next === "recurring_event") {
        return "The board stops running back to back: the next cycle opens on the cron and closes when its window is up, and the gap between windows is a pause players will see.";
      }
      if (next === "event" || next === "all_time") {
        return "A one-way door: neither kind cuts another cycle, so there is no boundary a later package could be applied at.";
      }
      return undefined;
    case "segmentation": {
      if (next === "tier") {
        const n = tierKeys(lb.tier_config).length;
        const leagues = n > 0 ? `${n} leagues` : "leagues";
        return `The next cycle is cut into ${leagues} and ranked inside each. The standings of the open cycle stay one flat table.`;
      }
      if (lb.segmentation === "tier") {
        return "The next cycle is one table again. The open cycle keeps the leagues it was cut with.";
      }
      return undefined;
    }
    case "retention_days":
      return "Applies to cycles finishing after the cut. Nothing already expired comes back.";
    case "scope":
      return "Rows already written belong to the entity the board has now. The next cycle ranks the other one — two answers that can only live in two different cycles.";
    default:
      return undefined;
  }
}
